/**
 * 文档管理类型定义
 *
 * 定义文档相关的TypeScript类型，供前端组件、服务和状态管理使用
 */

/**
 * 知识库类型枚举
 */
export enum KnowledgeBaseType {
  MATERIAL = 'material',
  SETTING = 'setting',
  CHAPTER = 'chapter',
  ALL = 'all'
}

/**
 * 文档状态枚举
 */
export enum DocumentStatus {
  UPLOADED = 'uploaded',
  PARSING = 'parsing',
  SLICING = 'slicing',
  INDEXING = 'indexing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  UNINDEXED = 'unindexed'
}

/**
 * 文档切片模式枚举
 */
export enum SliceMode {
  FIXED = 'fixed',
  PARAGRAPH = 'paragraph',
  HEADER = 'header'
}

/**
 * 素材类型枚举
 */
export enum MaterialType {
  NOVEL = 'novel',
  TECHNIQUE = 'technique',
  OTHER = 'other'
}

/**
 * 文档信息接口
 */
export interface Document {
  id: string;
  workspace_id: number;
  filename: string;
  file_path: string;
  file_size: number;
  content_type: string;
  material_type: MaterialType;
  md5_hash: string;
  status: DocumentStatus;
  error_message: string | null;
  upload_time: string;
}

/**
 * 文档上传请求接口
 */
export interface DocumentUploadRequest {
  workspace_id: number;
  file: File;
  materialType?: MaterialType;
}

/**
 * 文档上传响应接口
 */
export interface DocumentUploadResponse {
  id: string;
  filename: string;
  file_size: number;
  content_type: string;
  upload_time: string;
  status: DocumentStatus;
}

/**
 * 文档切片请求接口
 */
export interface DocumentSliceRequest {
  chunk_size?: number;
  overlap?: number;
  mode?: SliceMode;
}

/**
 * 文档切片响应接口
 */
export interface DocumentSliceResponse {
  chunks: Array<{
    index: number;
    content: string;
    char_count: number;
  }>;
  total_chunks: number;
}

/**
 * 文档解析响应接口
 */
export interface DocumentParseResponse {
  metadata: {
    title?: string;
    author?: string;
    created_time?: string;
    modified_time?: string;
    encoding?: string;
    char_count: number;
    paragraph_count?: number;
    word_count?: number;
  };
  structure?: {
    headings?: Array<{
      level: number;
      title: string;
      index: number;
    }>;
  };
  content: string;
}

/**
 * 文档状态响应接口
 */
export interface DocumentStatusResponse {
  id: string;
  filename: string;
  status: DocumentStatus;
  progress: number;
  error_message: string | null;
  current_stage: string;
}

/**
 * 素材库查询请求接口
 */
export interface MaterialQueryRequest {
  workspace_id: number;
  query_text: string;
  top_k?: number;
  document_id?: string;
}

/**
 * 素材库查询结果项接口
 */
export interface MaterialQueryResultItem {
  content: string;
  similarity: number;
  document_id: string;
  filename: string;
  chunk_index: number;
  metadata: Record<string, any>;
}

/**
 * 素材库查询响应接口
 */
export interface MaterialQueryResponse {
  results: MaterialQueryResultItem[];
  total: number;
  query_time_ms: number;
}

/**
 * 知识库健康状态项接口
 */
export interface KnowledgeBaseHealthItem {
  collection_name: string;
  vector_count: number;
  last_updated: string | null;
  status: 'healthy' | 'unhealthy' | 'unknown';
}

/**
 * 知识库健康响应接口
 */
export interface KnowledgeBaseHealthResponse {
  materials: KnowledgeBaseHealthItem;
  settings: KnowledgeBaseHealthItem;
  chapters: KnowledgeBaseHealthItem;
  overall_status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
}

/**
 * 文档列表查询参数接口
 */
export interface DocumentListQuery {
  workspace_id?: number;
  status?: DocumentStatus;
  page?: number;
  page_size?: number;
}

/**
 * 文档列表响应接口
 */
export interface DocumentListResponse {
  documents: Document[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * 文档更新请求接口
 */
export interface DocumentUpdate {
  filename?: string;
  material_type?: MaterialType;
  content?: string;
}
